import { useState } from 'react';
import { api } from '../api.js';
import { timeAgo } from '../util/time.js';

// T087 — Skill-gap snapshot panel. Lives on the Profile page; pulls from
// /api/ai/skill-gap. Backend caches for 7d so this is at most one
// Claude call per week.

export default function SkillGapPanel({ aiKeyReady }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async (refresh = false) => {
    if (!aiKeyReady) return;
    setLoading(true);
    setError('');
    try {
      const r = await api.get(`/ai/skill-gap${refresh ? '?refresh=true' : ''}`);
      setData(r);
    } catch (e) {
      setError(e?.body?.message || e?.message || 'Could not load skill gap');
    } finally {
      setLoading(false);
    }
  };

  if (!aiKeyReady) return null;

  if (!data && !loading && !error) {
    return (
      <section className="mt-6 rounded-xl border border-slate-800 bg-slate-900/30 p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <h2 className="text-sm font-medium text-brand-500">Skill-gap snapshot <span className="text-xs text-slate-500">(weekly)</span></h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Claude compares your stated skills against what shows up across your last 30 recommendations and surfaces the top gaps.
            </p>
          </div>
          <button
            type="button"
            onClick={() => load(false)}
            className="rounded-md border border-brand-700 px-3 py-1 text-xs text-brand-400 hover:bg-brand-600/10"
          >
            ✨ Run snapshot
          </button>
        </div>
      </section>
    );
  }

  if (loading && !data) {
    return (
      <section className="mt-6 rounded-xl border border-brand-700 bg-brand-600/5 p-4">
        <p className="text-sm text-brand-300">
          <span className="mr-2 inline-block h-2 w-2 animate-pulse rounded-full bg-brand-500" />
          Analyzing your recs against your profile skills…
        </p>
      </section>
    );
  }

  if (error && !data) {
    return (
      <section className="mt-6 rounded-xl border border-amber-700 bg-amber-950/15 p-4">
        <p className="text-sm text-amber-300">⚠ {error}</p>
        <button type="button" onClick={() => load(false)} className="mt-2 rounded border border-slate-700 px-2 py-0.5 text-xs text-slate-300 hover:border-brand-600">Retry</button>
      </section>
    );
  }

  return (
    <section className="mt-6 rounded-xl border border-slate-800 bg-slate-900/30 p-4">
      <div className="flex items-baseline justify-between gap-2">
        <div>
          <h2 className="text-sm font-medium text-brand-500">Skill-gap snapshot</h2>
          <p className="mt-0.5 text-[10px] uppercase tracking-widest text-slate-500">
            {data.generatedAt ? `generated ${timeAgo(data.generatedAt)}` : ''}{data.cached ? ' · cached' : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={() => load(true)}
          disabled={loading}
          className="rounded border border-slate-700 px-2 py-0.5 text-[10px] text-slate-400 hover:border-brand-600 hover:text-brand-400 disabled:opacity-50"
          title="Regenerate (costs ~$0.02)"
        >
          {loading ? '…' : '↻'}
        </button>
      </div>

      {data.overallAssessment && (
        <p className="mt-2 text-sm leading-relaxed text-slate-200">{data.overallAssessment}</p>
      )}

      {data.gaps?.length > 0 ? (
        <ul className="mt-3 space-y-2">
          {data.gaps.map((g, i) => (
            <li key={i} className="rounded border border-slate-800 bg-slate-950/40 p-3 text-sm">
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-medium text-slate-100">{g.skill}</span>
                <span className="shrink-0 rounded border border-brand-700 bg-brand-600/15 px-1.5 py-0.5 font-mono text-[10px] text-brand-400">
                  {g.appearsInPct}% of recs
                </span>
              </div>
              {g.why && <p className="mt-1 text-xs text-slate-300">{g.why}</p>}
              {g.howToCloseIt && <p className="mt-1 text-xs text-emerald-300">→ {g.howToCloseIt}</p>}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-xs text-emerald-400">No notable gaps — your profile is well aligned with the market you're hunting.</p>
      )}
    </section>
  );
}
